import type { EditorScreen } from '@/store/editor/types/screen';
import type { StylePack } from '@/themes/stylePack.types';
import { getStylePackById } from '@/themes/stylePacks';
import { resolveAdvanceDelayMs } from './autoAdvanceDelay';
import { resolveCtaDock, type CtaDockConfig } from './ctaDockStyle';
import { resolveQuizCta, type ResolvedQuizCta } from './quizCta';
import { useDelayedNavigation } from './navigationDelay';
import { quizHeaderForwardEnabled } from './quizHeader';
import {
  resolveChromeVisibility,
  screenHasAnswer,
  useForwardVisible,
  useVirtualKeyboardOpen,
  type ChromeVisibility,
} from './quizChromeState';
import {
  useScreenCanScroll,
  useScrollResetOnScreenChange,
} from './screenScroll';

/**
 * Одна точка входа для настроек шапки, нижней кнопки и перехода повтора
 * эталона Slimkit (пачка настроек 2) — чтобы большой файл QuizScreenWizard.tsx
 * звал ОДИН хук, а не семь. Все хуки внутри безусловные (правила хуков);
 * выключенные настройки не заводят ни таймеров, ни слушателей.
 */
export interface QuizChromeExtras {
  pack: StylePack | undefined;
  visibility: ChromeVisibility;
  /** Кнопка «вперёд» есть: поле шапки forwardButton, нет поля — пак. */
  forwardEnabled: boolean;
  /** «Вперёд» видна (ответ есть уже 300 мс). */
  forwardVisible: boolean;
  /** Нажатие кнопки перехода с паузой пака (0 — сразу, как было). */
  runNav: (action: () => boolean | void) => boolean;
  /** Прибитая нижняя кнопка; null — прежнее место. */
  dock: CtaDockConfig | null;
  /** Экран прокручивается (для подложки 'solid-when-scroll'). */
  canScroll: boolean;
  /** Шапка вне анимируемого корня экрана. */
  persistent: boolean;
  /**
   * Главная кнопка: положение и вид выключенной — поле воронки
   * `settings.quizCta` сильнее пака (слово владельца 21.10.2026, quizCta.ts);
   * положение у экрана со своим полем `ctaPlacement` — его.
   */
  cta: ResolvedQuizCta;
}

export function useQuizChromeExtras({
  stylePackId,
  quizHeader,
  screen,
  values,
  hiddenSet,
  isEditorPreview,
  quizCta,
}: {
  stylePackId: string | undefined;
  /** Сырое `settings.quizHeader`: поле forwardButton перебивает пак. */
  quizHeader?: unknown;
  screen: EditorScreen | undefined;
  values: Record<string, string>;
  hiddenSet: ReadonlySet<string>;
  isEditorPreview: boolean;
  /** Сырое `settings.quizCta` — разбирает `resolveQuizCta`. */
  quizCta?: unknown;
}): QuizChromeExtras {
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  const screenId = screen?.id;

  const keyboardOpen = useVirtualKeyboardOpen(
    pack?.chrome.hideOnKeyboard === true && !isEditorPreview,
  );
  const visibility = resolveChromeVisibility(pack, screen, keyboardOpen);

  // stylepack/header-counter-look: поле шапки перебивает пак (одна дверь).
  const forwardEnabled = quizHeaderForwardEnabled(pack?.chrome, quizHeader);
  const forwardReady =
    forwardEnabled && screen
      ? screenHasAnswer(screen.blocks, values, hiddenSet)
      : false;
  const forwardVisible = useForwardVisible(
    forwardEnabled,
    screenId ?? '',
    forwardReady,
  );

  const { run: runNav } = useDelayedNavigation(
    resolveAdvanceDelayMs(stylePackId),
    screenId,
  );

  useScrollResetOnScreenChange(
    pack?.states.resetScrollOnEnter === true,
    screenId,
  );

  // Положение: поле экрана → поле воронки → пак (stylepack/cta-screen-placement).
  const cta = resolveQuizCta(pack?.cta, quizCta, screen?.ctaPlacement);
  const dock = resolveCtaDock(pack, screen, cta.placement, cta.bottomOffset);
  const canScroll = useScreenCanScroll(
    dock?.surface === 'solid-when-scroll',
    screenId,
  );

  return {
    pack,
    visibility,
    forwardEnabled,
    forwardVisible,
    runNav,
    dock,
    canScroll,
    persistent: pack?.chrome.persistent === true,
    cta,
  };
}
